<script setup lang="ts">
import { Sheet, SheetContent, SheetHeader } from '@/components/ui/sheet'
import { DialogClose } from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import MercariFilterCommon, {
	type FilterType,
} from './_MercariFilterCommon.vue'

const props = defineProps({
	isOpen: {
		type: Boolean,
		default: false,
	},
	filterContent: {
		type: Object,
		required: true,
	},
	filterInstance: {
		type: Object,
		required: true,
	},
})

const emit = defineEmits(['update:isOpen'])

const {
	tempFilterState,
	isTempMinPriceValid,
	isTempMaxPriceValid,
	isTempPriceRangeValid,
	hasTempPriceError,
	handleTempPriceChange,
} = props.filterInstance

const {
	handleCheckboxChange,
	handleCategorySelect,
	handleClear,
	handleApply,
	handleOpenChange,
} = props.filterInstance.useFilterPanelLogic()
</script>

<template>
	<Sheet :open="isOpen" @update:open="value => handleOpenChange(value, emit)">
		<SheetContent
			side="right"
			class="sm:max-w-full flex w-[390px] flex-col border-none bg-white p-0"
		>
			<SheetHeader class="border-b border-n-200 px-24px py-16px">
				<div class="flex items-center justify-between">
					<h2 class="typography-h4-str w-full text-center text-n-950">
						{{ filterContent.title }}
					</h2>
					<DialogClose class="flex h-16px w-16px items-center justify-center">
						<BaseIcon name="close" class="h-20px w-20px" />
					</DialogClose>
				</div>
			</SheetHeader>

			<main class="flex-1 overflow-y-auto">
				<div class="space-y-24px divide-y divide-n-200 p-24px">
					<MercariFilterCommon
						:filter-content="filterContent"
						:filter-instance="props.filterInstance"
						:is-bottom-sheet="true"
						@checkbox-change="handleCheckboxChange"
						@category-select="handleCategorySelect"
					>
						<template #checkbox="{ option, checked, onChange }">
							<Checkbox
								:checked="checked"
								:value="option.id"
								@update:checked="onChange"
								class="h-20px w-20px"
							/>
						</template>
						<template #price>
							<div class="py-24px">
								<h4 class="typography-b3-reg mb-16px text-n-950">
									{{ filterContent.price.title }}
								</h4>
								<div class="flex gap-16px">
									<div class="flex-1">
										<input
											type="text"
											:value="tempFilterState.price.min"
											@input="(e: Event) => handleTempPriceChange('min', e)"
											:placeholder="filterContent.price.options[0].label"
											inputmode="numeric"
											pattern="[0-9]*"
											class="typography-s1-reg w-full rounded-lg border border-n-300 px-16px py-[10px] outline-none"
											:class="{
												'border-r-700':
													!isTempMinPriceValid || !isTempPriceRangeValid,
											}"
										/>
									</div>
									<div class="flex items-center">-</div>
									<div class="flex-1">
										<input
											type="text"
											:value="tempFilterState.price.max"
											@input="(e: Event) => handleTempPriceChange('max', e)"
											:placeholder="filterContent.price.options[1].label"
											inputmode="numeric"
											pattern="[0-9]*"
											class="typography-s1-reg w-full rounded-lg border border-n-300 px-16px py-[10px] outline-none"
											:class="{
												'border-r-700':
													!isTempMaxPriceValid || !isTempPriceRangeValid,
											}"
										/>
									</div>
								</div>
								<div
									class="typography-c2-reg mt-8px"
									:class="{ 'text-r-700': hasTempPriceError }"
								>
									{{ filterContent.price.description }}
								</div>
							</div>
						</template>
					</MercariFilterCommon>
				</div>
			</main>

			<footer class="flex gap-12px border-t border-n-200 bg-white p-16px">
				<BaseButton
					variant="secondary-orange"
					class="w-full"
					@click="() => handleClear(emit)"
				>
					{{ filterContent.buttons.clear }}
				</BaseButton>
				<BaseButton
					variant="primary"
					class="w-full"
					@click="() => handleApply(emit)"
				>
					{{ filterContent.buttons.apply }}
				</BaseButton>
			</footer>
		</SheetContent>
	</Sheet>
</template>
